
/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors } from '../../../ui';
// import { DebugOverlay } from '../../canvas/DebugOverlay';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { EditorSession } from '../../canvas/EditorSession';
import { Selection } from '../../canvas/Selection';
import { AttachPhysical } from '../../canvas/visualization/AttachPhysical';
import { useComponentObserver } from '../../canvas/visualization/ComponentObserver';
import { useResolve } from '../../Container';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { Tray } from './Tray';
import { trayToggleEditing } from './TrayCommands';

type ButtonProps = {
    disabled?: boolean,
    left?: string,
    right?: string,
    top?: string,
    bottom?: string,
    transform?: string,
    onAction: () => void,
    children?: React.ReactNode,
};

const ButtonFrame = styled.div<{ disabled?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    position: absolute;
    padding: 0.125rem 0.25rem;
    font-weight: 450;
    font-size: .875rem;
    border: 2px solid ${Colors.primaryLight};
    border-radius: 4px;
    background: white;
    color: ${Colors.primaryLight};
    pointer-events: all;
    user-select: none;
    white-space: nowrap;

    &:hover {
        background: ${Colors.primaryLight};
        color: white;
        cursor: pointer;
    }

    &:active {
        background: ${Colors.primary};
        color: white;
        border-color: ${Colors.primary};
    };

    ${p => p.disabled && css`
        pointer-events: none;
        opacity: 0.5;
        filter: grayscale(50%);
    `}
`;

const LabelButton = ({ children, disabled, onAction, ...style }: ButtonProps) =>
    <ButtonFrame disabled={disabled} onClick={onAction} style={style}>
        {children}
    </ButtonFrame>;

const useTraysBeingEdited = () => {
    const objects = useResolve(ObjectManager);
    const [trays, setTrays] = useState<Tray[]>([]);

    useEffect(() => {
        setTrays([...objects.getAll(Tray)].filter(x => x.editing));
    }, []);

    useEffect(() => {
        return objects.createTrigger('TrayWidget.OnEditingChange')
            .for(Tray)
            .onChangesTo('editing')
            .doForEach(tray => {
                if (tray.alive && tray?.editing) {
                    setTrays(s => [...new Set([...s, tray])]);
                }
                else {
                    setTrays(s => s.filter(t => t !== tray));
                }
            });
    }, []);

    useEffect(() => {
        return objects.on('Tray', 'released', ([tray]) => {
            if (trays.includes(tray as Tray)) {
                setTrays(s => s.filter(t => t !== tray));
            }
        });
    }, []);

    return trays.filter(x => x.alive);
};

const useSelectedTray = () => {
    const session = useResolve(EditorSession);
    const [tray, setTray] = useState(null as Tray | null);

    useEffect(() => {
        return session.observe(Selection, x => {
            const next = x.firstAndOnly()?.as(Tray);
            if (next && next.alive) {
                setTray(next);
            }
            else if (tray) {
                setTray(null);
            }
        });
    }, [tray]);

    return !!tray ? [tray] : [];
};

const TrayEditingWidget = React.memo(({ tray }: { tray: Tray }) => {

    useComponentObserver(tray);

    const dispatch = useResolve(CommandDispatcher);

    const onSetEditing = (value: boolean) => {
        dispatch(trayToggleEditing, [[tray.id], value]);
    };

    return <AttachPhysical object={tray}>
        <Flexer absolute inset flex border={`solid .0625rem ${Colors.primaryLight}`} boxShadow={`0 0 0 .0625rem ${Colors.primaryLight}`}>
            {!tray.editing &&
                <LabelButton left="50%" top="100%" transform="translate(-50%, -50%)" onAction={() => onSetEditing(true)}>
                    Edit Items
                </LabelButton>
            }
            {tray.editing &&
                <LabelButton left="50%" top="100%" transform="translate(-50%, -50%)" onAction={() => onSetEditing(false)}>
                    <Icon size="1.25" src="deqo://icons/hero/check" />
                    Done Editing
                </LabelButton>
            }
        </Flexer>
    </AttachPhysical>;
});

export const TrayWidget = React.memo(() => {

    const trays = [
        ...useTraysBeingEdited(),
        ...useSelectedTray()
    ];

    // const dispatch = useResolve(CommandDispatcher);
    // const objects = useResolve(ObjectManager);
    // const ai = useEditorSessionObserver(AdapterInfo);
    // const updateKey = `trayAdapter:${ai.target}`;
    // const [changes, setChanges] = useState(false);
    // debugger;
    // const subject = objects.getMaybe(info.target, Tray);
    // if (!subject) return null;

    // return <Flexer pointerEvents="none" border={`solid .0625rem ${Colors.primaryLight}`}>
    // <AttachPhysical object={subject}>
    //     <Flexer absolute inset flex border={`solid .0625rem ${Colors.primaryLight}`}>
    //         <LabelButton label="Tray" left="0" top="0" transform="translate(0, -10px)" onAction={() => { }} />
    //     </Flexer>
    // </AttachPhysical>
    // </Flexer>;


    return <Flexer id="trayWidget" inset="0" pointerEvents="none">
        {trays.map(tray => <TrayEditingWidget key={tray.id} tray={tray} />)}
    </Flexer>;
});

